import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { exportAPI, schedulesAPI, courseAPI } from '@/lib/api';
import api from '@/lib/api';
import { WorkspaceLayout } from '@/components/layout/WorkspaceLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { FileSpreadsheet, FileText, Download, Loader2, BarChart3, CalendarIcon, GraduationCap, ClipboardCheck } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { toast } from 'sonner';

const ReportsPage = () => {
  const { workspaceId } = useParams();
  
  const [schedules, setSchedules] = useState([]);
  const [courses, setCourses] = useState([]);
  const [evaluations, setEvaluations] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Attendance filters
  const [attendanceFilters, setAttendanceFilters] = useState({
    period: 'custom',
    startDate: null,
    endDate: null,
    specificDate: null,
    courseId: '',
    scheduleId: '',
    format: 'excel'
  });
  
  // Grades filters
  const [gradesFilters, setGradesFilters] = useState({
    courseId: '',
    evaluationId: '',
    evaluationType: '',
    startDate: null,
    endDate: null,
    format: 'excel'
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [schedulesRes, coursesRes, evaluationsRes] = await Promise.all([
        schedulesAPI.getAll(workspaceId),
        courseAPI.list(workspaceId),
        api.get('/evaluations')
      ]);
      setSchedules(schedulesRes.data);
      setCourses(coursesRes.data);
      setEvaluations(evaluationsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const handleAttendanceExport = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('format', attendanceFilters.format);
      
      if (attendanceFilters.period !== 'custom') {
        params.append('period', attendanceFilters.period);
      }
      if (attendanceFilters.specificDate) {
        params.append('specific_date', format(attendanceFilters.specificDate, 'yyyy-MM-dd'));
        params.append('period', 'day');
      }
      if (attendanceFilters.startDate) {
        params.append('start_date', format(attendanceFilters.startDate, 'yyyy-MM-dd'));
      }
      if (attendanceFilters.endDate) {
        params.append('end_date', format(attendanceFilters.endDate, 'yyyy-MM-dd'));
      }
      if (attendanceFilters.courseId && attendanceFilters.courseId !== 'all') {
        params.append('course_id', attendanceFilters.courseId);
      }
      if (attendanceFilters.scheduleId && attendanceFilters.scheduleId !== 'all') {
        params.append('schedule_id', attendanceFilters.scheduleId);
      }

      const response = await api.get(`/export/attendance?${params.toString()}`, {
        responseType: 'blob'
      });
      
      downloadFile(response.data, `rapport_presence_${format(new Date(), 'yyyyMMdd')}`, attendanceFilters.format);
      toast.success('Export téléchargé avec succès');
    } catch (error) {
      console.error('Error exporting:', error);
      toast.error("Erreur lors de l'export");
    } finally {
      setLoading(false);
    }
  };

  const handleGradesExport = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('format', gradesFilters.format);
      
      if (gradesFilters.courseId && gradesFilters.courseId !== 'all') {
        params.append('course_id', gradesFilters.courseId);
      }
      if (gradesFilters.evaluationId && gradesFilters.evaluationId !== 'all') {
        params.append('evaluation_id', gradesFilters.evaluationId);
      }
      if (gradesFilters.evaluationType && gradesFilters.evaluationType !== 'all') {
        params.append('evaluation_type', gradesFilters.evaluationType);
      }
      if (gradesFilters.startDate) {
        params.append('start_date', format(gradesFilters.startDate, 'yyyy-MM-dd'));
      }
      if (gradesFilters.endDate) {
        params.append('end_date', format(gradesFilters.endDate, 'yyyy-MM-dd'));
      }

      const response = await api.get(`/export/grades?${params.toString()}`, {
        responseType: 'blob'
      });
      
      downloadFile(response.data, `rapport_notes_${format(new Date(), 'yyyyMMdd')}`, gradesFilters.format);
      toast.success('Export téléchargé avec succès');
    } catch (error) {
      console.error('Error exporting:', error);
      toast.error("Erreur lors de l'export");
    } finally {
      setLoading(false);
    }
  };

  const downloadFile = (blob, filename, format) => {
    const ext = format === 'excel' ? 'xlsx' : format;
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${filename}.${ext}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const evaluationTypes = [
    { value: 'tp', label: 'TP' },
    { value: 'exam', label: 'Examen' },
    { value: 'continuous', label: 'Contrôle continu' },
    { value: 'project', label: 'Projet' },
    { value: 'quiz', label: 'Quiz' }
  ];

  return (
    <WorkspaceLayout>
    <div className="space-y-6" data-testid="reports-page">
      <div>
        <h1 className="text-2xl font-bold gradient-brand-text">Rapports</h1>
        <p className="text-slate-500">Exportez vos données avec des filtres avancés</p>
      </div>

      <Tabs defaultValue="attendance" className="space-y-6">
        <TabsList className="grid w-full grid-cols-2 max-w-md">
          <TabsTrigger value="attendance" data-testid="tab-attendance">
            <ClipboardCheck className="h-4 w-4 mr-2" />
            Présences
          </TabsTrigger>
          <TabsTrigger value="grades" data-testid="tab-grades">
            <GraduationCap className="h-4 w-4 mr-2" />
            Notes
          </TabsTrigger>
        </TabsList>

        {/* Attendance Reports */}
        <TabsContent value="attendance">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Download className="h-5 w-5" />
                  Export des présences
                </CardTitle>
                <CardDescription>
                  Filtrez et exportez les données de présence
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Period selection */}
                <div className="space-y-2">
                  <Label>Période</Label>
                  <Select 
                    value={attendanceFilters.period} 
                    onValueChange={(v) => setAttendanceFilters({...attendanceFilters, period: v, specificDate: null})}
                  >
                    <SelectTrigger data-testid="period-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="day">Jour spécifique</SelectItem>
                      <SelectItem value="week">Cette semaine</SelectItem>
                      <SelectItem value="month">Ce mois</SelectItem>
                      <SelectItem value="year">Cette année</SelectItem>
                      <SelectItem value="custom">Période personnalisée</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Specific date picker */}
                {attendanceFilters.period === 'day' && (
                  <div className="space-y-2">
                    <Label>Date</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-start text-left">
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {attendanceFilters.specificDate 
                            ? format(attendanceFilters.specificDate, 'PPP', { locale: fr })
                            : 'Sélectionner une date'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar
                          mode="single"
                          selected={attendanceFilters.specificDate}
                          onSelect={(date) => setAttendanceFilters({...attendanceFilters, specificDate: date})}
                          locale={fr}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                )}

                {/* Custom date range */}
                {attendanceFilters.period === 'custom' && (
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Date début</Label>
                      <Input
                        type="date"
                        value={attendanceFilters.startDate ? format(attendanceFilters.startDate, 'yyyy-MM-dd') : ''}
                        onChange={(e) => setAttendanceFilters({...attendanceFilters, startDate: e.target.value ? new Date(e.target.value) : null})}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Date fin</Label>
                      <Input
                        type="date"
                        value={attendanceFilters.endDate ? format(attendanceFilters.endDate, 'yyyy-MM-dd') : ''}
                        onChange={(e) => setAttendanceFilters({...attendanceFilters, endDate: e.target.value ? new Date(e.target.value) : null})}
                      />
                    </div>
                  </div>
                )}

                {/* Course filter */}
                <div className="space-y-2">
                  <Label>Cours (optionnel)</Label>
                  <Select 
                    value={attendanceFilters.courseId || 'all'} 
                    onValueChange={(v) => setAttendanceFilters({...attendanceFilters, courseId: v === 'all' ? '' : v})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Tous les cours" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tous les cours</SelectItem>
                      {courses.map(course => (
                        <SelectItem key={course.id} value={course.id}>
                          {course.title}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Format selection */}
                <div className="space-y-2">
                  <Label>Format</Label>
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { value: 'excel', label: 'Excel', icon: FileSpreadsheet },
                      { value: 'csv', label: 'CSV', icon: FileText },
                      { value: 'pdf', label: 'PDF', icon: FileText }
                    ].map(({ value, label, icon: Icon }) => (
                      <Button
                        key={value}
                        type="button"
                        variant={attendanceFilters.format === value ? 'default' : 'outline'}
                        className="justify-start"
                        onClick={() => setAttendanceFilters({...attendanceFilters, format: value})}
                      >
                        <Icon className="mr-2 h-4 w-4" />
                        {label}
                      </Button>
                    ))}
                  </div>
                </div>

                <Button
                  className="w-full"
                  onClick={handleAttendanceExport}
                  disabled={loading}
                  data-testid="export-attendance-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Export en cours...
                    </>
                  ) : (
                    <>
                      <Download className="mr-2 h-4 w-4" />
                      Télécharger
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>

            {/* Info card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="h-5 w-5" />
                  Rapports de présence disponibles
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <h4 className="font-medium text-slate-900">Rapport journalier</h4>
                    <p className="text-sm text-slate-500 mt-1">
                      Liste des présences pour une date spécifique
                    </p>
                  </div>
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <h4 className="font-medium text-slate-900">Rapport hebdomadaire</h4>
                    <p className="text-sm text-slate-500 mt-1">
                      Synthèse des présences de la semaine en cours
                    </p>
                  </div>
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <h4 className="font-medium text-slate-900">Rapport mensuel</h4>
                    <p className="text-sm text-slate-500 mt-1">
                      Récapitulatif mensuel avec taux de présence
                    </p>
                  </div>
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <h4 className="font-medium text-slate-900">Période personnalisée</h4>
                    <p className="text-sm text-slate-500 mt-1">
                      Choisissez vos dates de début et fin
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Grades Reports */}
        <TabsContent value="grades">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Download className="h-5 w-5" />
                  Export des notes
                </CardTitle>
                <CardDescription>
                  Filtrez et exportez les données de notes
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Course filter */}
                <div className="space-y-2">
                  <Label>Cours</Label>
                  <Select 
                    value={gradesFilters.courseId || 'all'} 
                    onValueChange={(v) => setGradesFilters({...gradesFilters, courseId: v === 'all' ? '' : v, evaluationId: ''})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Tous les cours" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tous les cours</SelectItem>
                      {courses.map(course => (
                        <SelectItem key={course.id} value={course.id}>
                          {course.title}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Evaluation type filter */}
                <div className="space-y-2">
                  <Label>Type d'évaluation</Label>
                  <Select 
                    value={gradesFilters.evaluationType || 'all'} 
                    onValueChange={(v) => setGradesFilters({...gradesFilters, evaluationType: v === 'all' ? '' : v})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Tous les types" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tous les types</SelectItem>
                      {evaluationTypes.map(type => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Specific evaluation */}
                <div className="space-y-2">
                  <Label>Évaluation spécifique</Label>
                  <Select 
                    value={gradesFilters.evaluationId || 'all'} 
                    onValueChange={(v) => setGradesFilters({...gradesFilters, evaluationId: v === 'all' ? '' : v})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Toutes les évaluations" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Toutes les évaluations</SelectItem>
                      {evaluations
                        .filter(e => !gradesFilters.courseId || e.course_id === gradesFilters.courseId)
                        .map(evaluation => (
                          <SelectItem key={evaluation.evaluation_id} value={evaluation.evaluation_id}>
                            {evaluation.title}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Date range */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Date début</Label>
                    <Input
                      type="date"
                      value={gradesFilters.startDate ? format(gradesFilters.startDate, 'yyyy-MM-dd') : ''}
                      onChange={(e) => setGradesFilters({...gradesFilters, startDate: e.target.value ? new Date(e.target.value) : null})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Date fin</Label>
                    <Input
                      type="date"
                      value={gradesFilters.endDate ? format(gradesFilters.endDate, 'yyyy-MM-dd') : ''}
                      onChange={(e) => setGradesFilters({...gradesFilters, endDate: e.target.value ? new Date(e.target.value) : null})}
                    />
                  </div>
                </div>

                {/* Format selection */}
                <div className="space-y-2">
                  <Label>Format</Label>
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { value: 'excel', label: 'Excel', icon: FileSpreadsheet },
                      { value: 'csv', label: 'CSV', icon: FileText },
                      { value: 'pdf', label: 'PDF', icon: FileText }
                    ].map(({ value, label, icon: Icon }) => (
                      <Button
                        key={value}
                        type="button"
                        variant={gradesFilters.format === value ? 'default' : 'outline'}
                        className="justify-start"
                        onClick={() => setGradesFilters({...gradesFilters, format: value})}
                      >
                        <Icon className="mr-2 h-4 w-4" />
                        {label}
                      </Button>
                    ))}
                  </div>
                </div>

                <Button
                  className="w-full"
                  onClick={handleGradesExport}
                  disabled={loading}
                  data-testid="export-grades-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Export en cours...
                    </>
                  ) : (
                    <>
                      <Download className="mr-2 h-4 w-4" />
                      Télécharger
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>

            {/* Info card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="h-5 w-5" />
                  Rapports de notes disponibles
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="p-4 bg-emerald-50 rounded-lg border-l-4 border-emerald-500">
                    <h4 className="font-medium text-emerald-900">Par cours</h4>
                    <p className="text-sm text-emerald-700 mt-1">
                      Toutes les notes d'un cours spécifique
                    </p>
                  </div>
                  <div className="p-4 bg-blue-50 rounded-lg border-l-4 border-blue-500">
                    <h4 className="font-medium text-blue-900">Par type d'évaluation</h4>
                    <p className="text-sm text-blue-700 mt-1">
                      Filtrer par TP, examen, projet, etc.
                    </p>
                  </div>
                  <div className="p-4 bg-purple-50 rounded-lg border-l-4 border-purple-500">
                    <h4 className="font-medium text-purple-900">Par période</h4>
                    <p className="text-sm text-purple-700 mt-1">
                      Notes sur une plage de dates
                    </p>
                  </div>
                  <div className="p-4 bg-amber-50 rounded-lg border-l-4 border-amber-500">
                    <h4 className="font-medium text-amber-900">Évaluation spécifique</h4>
                    <p className="text-sm text-amber-700 mt-1">
                      Notes d'une évaluation particulière
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
    </WorkspaceLayout>
  );
};

export default ReportsPage;
